/**
 * ThemeUpdateNotice Component
 *
 * In-app notification banner for theme update announcements.
 * Displays when feature flag indicates announcement is active.
 *
 * Features:
 * - Banner appears at top of app (below header, above main content)
 * - Shows only when feature flag indicates announcement is active
 * - Dismissible with localStorage persistence
 * - Slide-in animation on mount, smooth slide-out on dismiss
 * - Respects prefers-reduced-motion for accessibility
 * - Links to help page or opens modal with details
 */

import { useEffect, useState, useCallback } from 'react';
import { X, Info, Sparkles } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import {
  getUnreadNotifications,
  markNotificationAsRead,
  dismissNotificationLocally,
  isNotificationDismissed,
  type UserNotification
} from '@/lib/userCommunication';

interface ThemeUpdateNoticeProps {
  /** Feature flag name to check for announcement active state */
  featureFlagName?: string;
  /** Whether to show the notice even if no unread notifications exist */
  forceShow?: boolean;
  /** Custom class name for the container */
  className?: string;
  /** Callback when notice is dismissed */
  onDismiss?: () => void;
  /** Notification type filter */
  type?: UserNotification['type'];
}

/**
 * Feature flag response type
 */
interface FeatureFlagResponse {
  name: string;
  is_enabled: boolean;
  rollout_percentage: number;
  config: Record<string, unknown>;
}

/**
 * Theme update notice notification component
 * Displays a dismissible banner announcing theme updates
 */
export function ThemeUpdateNotice({
  featureFlagName = 'theme_announcement_active',
  forceShow = false,
  className = '',
  onDismiss,
  type = 'announcement'
}: ThemeUpdateNoticeProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [notification, setNotification] = useState<UserNotification | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Local storage key for dismissed state
  const DISMISSED_KEY = 'theme-update-notice-dismissed';
  const NOTIFICATION_TYPE_KEY = 'theme-update-notice-type';

  /**
   * Check if user prefers reduced motion
   */
  const prefersReducedMotion = useCallback(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  /**
   * Check if the notice has been dismissed
   */
  const isDismissed = useCallback((): boolean => {
    if (typeof window === 'undefined') return false;

    const dismissed = localStorage.getItem(DISMISSED_KEY);
    const notificationType = localStorage.getItem(NOTIFICATION_TYPE_KEY);

    // If the notification type changed, show the notice again
    if (notificationType !== type) {
      return false;
    }

    return dismissed === 'true';
  }, [type]);

  /**
   * Mark the notice as dismissed
   */
  const markAsDismissed = useCallback(() => {
    if (typeof window === 'undefined') return;

    localStorage.setItem(DISMISSED_KEY, 'true');
    localStorage.setItem(NOTIFICATION_TYPE_KEY, type);
  }, [type]);

  /**
   * Check feature flag for announcement active state
   */
  const checkFeatureFlag = useCallback(async (): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('feature_flags')
        .select('*')
        .eq('name', featureFlagName)
        .maybeSingle();

      if (error) {
        console.error('Error checking feature flag:', error);
        return false;
      }

      const flag = data as FeatureFlagResponse | null;
      return flag?.is_enabled || flag?.rollout_percentage > 0 || false;
    } catch {
      return false;
    }
  }, [featureFlagName]);

  /**
   * Fetch unread notification of the specified type
   */
  const fetchNotification = useCallback(async () => {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const notifications = await getUnreadNotifications(user.id);
      const matchingNotification = notifications.find(n => n.type === type);

      if (matchingNotification) {
        setNotification(matchingNotification);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error fetching notification:', error);
      return false;
    }
  }, [type]);

  /**
   * Initialize: check conditions and show notice if appropriate
   */
  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);

      // Check if already dismissed
      if (isDismissed() && !forceShow) {
        setIsLoading(false);
        return;
      }

      // Check feature flag
      const featureFlagActive = await checkFeatureFlag();

      if (!featureFlagActive && !forceShow) {
        setIsLoading(false);
        return;
      }

      // Fetch notification or show default
      const hasNotification = await fetchNotification();

      if (hasNotification || featureFlagActive || forceShow) {
        // Small delay for smooth animation
        setTimeout(() => {
          setIsVisible(true);
        }, prefersReducedMotion() ? 0 : 300);
      }

      setIsLoading(false);
    };

    initialize();
  }, [checkFeatureFlag, fetchNotification, forceShow, isDismissed, prefersReducedMotion]);

  /**
   * Handle dismiss action
   */
  const handleDismiss = useCallback(async () => {
    setIsAnimatingOut(true);

    // Wait for animation to complete
    const animationDuration = prefersReducedMotion() ? 0 : 300;
    setTimeout(() => {
      setIsVisible(false);
      markAsDismissed();

      if (onDismiss) {
        onDismiss();
      }

      // Mark notification as read in database
      if (notification) {
        markNotificationAsRead(notification.id).catch(console.error);
        dismissNotificationLocally(notification.id);
      }
    }, animationDuration);
  }, [notification, onDismiss, markAsDismissed, prefersReducedMotion]);

  /**
   * Handle click on "Learn More" link
   */
  const handleLearnMore = useCallback(() => {
    // Open help page or show modal with details
    // For now, just scroll to top or open a help section
    window.open('https://meucontrolefinanceiro.com/ajuda/atualizacao-tema', '_blank');
  }, []);

  // Don't render if loading, not visible, or dismissed
  if (isLoading) {
    return null;
  }

  if (!isVisible) {
    return null;
  }

  // Animation styles
  const animationStyles = prefersReducedMotion()
    ? {}
    : {
        animation: isAnimatingOut
          ? 'slideOut 0.3s ease-out forwards'
          : 'slideIn 0.3s ease-out forwards'
      };

  return (
    <>
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-100%);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideOut {
          from {
            opacity: 1;
            transform: translateY(0);
          }
          to {
            opacity: 0;
            transform: translateY(-100%);
          }
        }
      `}</style>
      <div
        className={`fixed top-16 left-0 right-0 z-50 px-4 py-2 ${className}`}
        style={animationStyles}
      >
        <Card className="max-w-4xl mx-auto border-l-4 border-l-amber-500 bg-gradient-to-r from-amber-50 to-amber-100/50 dark:from-amber-950/50 dark:to-amber-900/20 dark:border-l-amber-600">
          <div className="flex items-center justify-between p-4">
            {/* Icon and content */}
            <div className="flex items-start gap-3 flex-1">
              {/* Icon */}
              <div className="flex-shrink-0 mt-0.5">
                <div className="p-2 bg-amber-500 rounded-full dark:bg-amber-600">
                  <Sparkles className="h-4 w-4 text-white" />
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-100 flex items-center gap-2">
                  {notification?.title || 'Nova aparencia do modo escuro chegando!'}
                  <span className="px-2 py-0.5 text-xs font-medium bg-amber-200 text-amber-800 rounded-full dark:bg-amber-800 dark:text-amber-100">
                    Novidade
                  </span>
                </h3>
                <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">
                  {notification?.message || 'Estamos melhorando a experiencia do modo escuro com cores mais confortaveis. Saiba mais sobre as atualizacoes.'}
                </p>
                <div className="flex items-center gap-3 mt-2">
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-amber-900 dark:text-amber-100 underline underline-offset-4"
                    onClick={handleLearnMore}
                  >
                    Saiba mais
                  </Button>
                  <span className="text-xs text-amber-700 dark:text-amber-300">
                    {notification?.created_at
                      ? new Date(notification.created_at).toLocaleDateString('pt-BR')
                      : new Date().toLocaleDateString('pt-BR')
                    }
                  </span>
                </div>
              </div>
            </div>

            {/* Dismiss button */}
            <button
              onClick={handleDismiss}
              className="flex-shrink-0 p-1.5 rounded-full hover:bg-amber-200 dark:hover:bg-amber-800 transition-colors"
              aria-label="Dispensar notificacao"
              type="button"
            >
              <X className="h-4 w-4 text-amber-700 dark:text-amber-300" />
            </button>
          </div>
        </Card>
      </div>
    </>
  );
}

/**
 * Hook to programmatically control the theme update notice
 * Useful for showing the notice from other components
 */
export function useThemeUpdateNotice() {
  const [isVisible, setIsVisible] = useState(false);

  const show = useCallback(() => {
    // Clear dismissed state to show notice again
    localStorage.removeItem('theme-update-notice-dismissed');
    setIsVisible(true);

    // Trigger re-render by dispatching storage event
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'theme-update-notice-dismissed',
      newValue: null
    }));
  }, []);

  const hide = useCallback(() => {
    localStorage.setItem('theme-update-notice-dismissed', 'true');
    setIsVisible(false);
  }, []);

  const reset = useCallback(() => {
    localStorage.removeItem('theme-update-notice-dismissed');
    localStorage.removeItem('theme-update-notice-type');
    setIsVisible(false);
  }, []);

  return {
    isVisible,
    show,
    hide,
    reset
  };
}

/**
 * Standalone hook for checking theme announcement status
 */
export function useThemeAnnouncement() {
  const [isActive, setIsActive] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const { data, error } = await supabase
          .from('feature_flags')
          .select('is_enabled, rollout_percentage')
          .eq('name', 'theme_announcement_active')
          .maybeSingle();

        if (!error && data) {
          const flag = data as FeatureFlagResponse;
          setIsActive(flag.is_enabled || flag.rollout_percentage > 0);
        }
      } catch {
        setIsActive(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkStatus();
  }, []);

  return { isActive, isLoading };
}

export default ThemeUpdateNotice;
